import { useEffect } from 'react';

import { genders } from 'lib/constants/constants';
import { cn, ia, optionify } from 'lib/helpers/utility';
import { useRoleList } from 'lib/hooks/queries/roles/useRoleList';
import { useGetUserTrainings } from 'lib/hooks/queries/trainings/useGetTrainings';
import { useProfessionGroups } from 'lib/hooks/queries/specialty/useProfessionGroups';

import Input from 'components/shared/Input/Input';
import Select from 'components/shared/Select/Select';
import Allowed from 'components/shared/Permissions/Allowed';
import ColorPicker from 'components/shared/ColorPicker/ColorPicker';
import DateInputPicker from 'components/shared/DateInputPicker/DateInputPicker';
import SectionList from 'components/practice/UserPreferences/components/SectionList';

const PersonalInformationSection = ({ formik, practiceId, userId }) => {
  const { data } = useRoleList({ params: { practiceId } });
  const { data: specialities } = useProfessionGroups({
    params: {}
  });
  const { data: user_trainings } = useGetUserTrainings({
    params: { practiceId, userId },
    dependencies: [practiceId, userId]
  });
  const roleOptions = optionify(data?.role_list);
  const professionGroupOptions = optionify(specialities?.specialties || []);
  const trainingOptions = optionify(
    user_trainings?.meetings || [],
    'name',
    'wizard_step_definition_id'
  );

  const handlePaymentRateChange = (e) => {
    const inputValue = e.target.value;
    let cleanValue = inputValue.replace(/[^0-9.]/g, '');

    if (cleanValue === '' || cleanValue === '.') {
      formik.setFieldValue('payment_rate', '');
      return;
    }

    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }

    const finalParts = cleanValue.split('.');
    if (finalParts[1] && finalParts[1].length > 2) {
      cleanValue = finalParts[0] + '.' + finalParts[1].slice(0, 2);
    }

    formik.setFieldValue('payment_rate', cleanValue);
  };

  const { setFieldValue } = formik;

  useEffect(() => {
    if (
      formik.values.role &&
      roleOptions.length > 0 &&
      !roleOptions.find((role) => role.value === formik.values.role)
    ) {
      setFieldValue('role', null);
    }
  }, [formik.values.role, roleOptions, setFieldValue]);

  useEffect(() => {
    if (user_trainings?.meetings) {
      const assignedIds = user_trainings.meetings
        .filter((m) => m.is_assigned)
        .map((m) => m.wizard_step_definition_id);
      setFieldValue('trainings', assignedIds);
    }
  }, [user_trainings?.meetings, setFieldValue]);

  return (
    <SectionList
      className="gap-3"
      title="Basic information"
      description="Enter personal and role details to identify and assign the staff member within your organization.">
      <div className="flex w-full gap-3">
        <div className="flex w-1/2 gap-3">
          <ColorPicker
            color={formik.values.color}
            buttonClassName="h-[39px]"
            onChange={(e) => formik.setFieldValue('color', e)}
          />
          <Input
            label="First name"
            placeholder="Enter first name"
            name="f_name"
            className="w-full"
            value={formik.values.f_name}
            onChange={formik.handleChange}
            error={formik.errors.f_name}
          />
        </div>
        <div className="w-1/2">
          <Input
            label="Middle name"
            placeholder="Enter middle name"
            name="m_name"
            isOptional
            className="w-full"
            value={formik.values.m_name}
            onChange={formik.handleChange}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Last name"
          placeholder="Enter last name"
          name="l_name"
          value={formik.values.l_name}
          onChange={formik.handleChange}
          error={formik.errors.l_name}
        />
        <Select
          value={
            formik.values.role && {
              label: roleOptions.find((role) => role.value === formik.values.role)?.label
            }
          }
          error={formik.errors.role}
          onChange={(option) => formik.setFieldValue('role', option?.value)}
          label="Role"
          inputId="select-role"
          options={roleOptions}
          placeholder="Select role"
        />
        <DateInputPicker
          value={formik.values.dob}
          onChange={(date) => formik.setFieldValue('dob', date)}
          label="Date of birth"
          isDateOnly
          buttonClassName={cn('w-full', formik.errors.dob && 'border-danger-300')}
        />
        {formik.errors.dob && <p className="pt-2 text-sm text-danger-500">{formik.errors.dob}</p>}
        <Select
          options={genders}
          name="gender"
          value={
            formik.values.gender && {
              label: genders.find((g) => g.value === formik.values.gender)?.label
            }
          }
          onChange={(option) => formik.setFieldValue('gender', option?.value)}
          label="Gender"
          inputId="select-gender"
          placeholder="Select gender"
          error={formik.errors.gender}
        />
        <Input
          name="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          label="Email"
          placeholder="Enter email address"
          error={formik.errors.email}
        />
        <Input
          name="phone"
          value={formik.values.phone}
          onChange={formik.handleChange}
          label="Phone number"
          placeholder="Enter phone number"
          error={formik.errors.phone}
        />
        <Select
          value={
            formik.values.profession_group_id && {
              label: professionGroupOptions.find(
                (group) => group.value === formik.values.profession_group_id
              )?.label
            }
          }
          error={formik.errors.profession_group_id}
          onChange={(option) => formik.setFieldValue('profession_group_id', option?.value)}
          label="Profession group"
          inputId="select-profession-group"
          options={professionGroupOptions}
          placeholder="Select profession group"
        />
        <Allowed requiredPermissions="member.payment_access">
          <Input
            name="payment_rate"
            value={'$' + (formik.values.payment_rate ?? '')}
            onChange={handlePaymentRateChange}
            label="Payment rate"
            placeholder="Enter payment rate"
            error={formik.errors.payment_rate}
            actionButtonText="/hour"
            actionButtonClick={() => {}}
          />
        </Allowed>
      </div>
      {trainingOptions.length > 0 && (
        <div>
          <Select
            value={trainingOptions.filter((t) => formik.values.trainings?.includes(t.value)) || []}
            isMulti
            error={formik.errors.trainings}
            onChange={(option) =>
              formik.setFieldValue('trainings', ia(option) ? option.map((o) => o.value) : [])
            }
            label="Trainings needed"
            options={trainingOptions}
            placeholder="Select trainings needed"
          />
        </div>
      )}
    </SectionList>
  );
};

export default PersonalInformationSection;
